import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Typography, 
  Button, 
  Alert,
  CircularProgress,
  Box,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  Security as SecurityIcon, 
  CheckCircle as CheckCircleIcon,
  Fingerprint as FingerprintIcon,
  PhoneAndroid as PhoneIcon
} from '@mui/icons-material';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import WebAuthnSetup from '../components/WebAuthn/WebAuthnSetup';
import TOTPSetup from '../components/TOTP/TOTPSetup';
import ThemeBackground from '../components/Layout/ThemeBackground';

function MfaSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateToken } = useAuth();
  
  // Form states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaSetup, setMfaSetup] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);
  const [mfaMethod, setMfaMethod] = useState<'webauthn' | 'totp'>('totp'); // Default to TOTP

  // Get setup token and user info from login flow
  useEffect(() => {
    const setupToken = localStorage.getItem('mfaSetupToken');
    const fromLogin = (location.state as any)?.fromLogin;
    
    if (!setupToken || !fromLogin) {
      // Redirect to login if no setup token
      navigate('/login', { 
        state: { 
          message: 'Please login to access MFA setup' 
        }
      });
      return;
    }

    // Set the token in API client for authenticated requests
    api.defaults.headers.common['Authorization'] = `Bearer ${setupToken}`;
    setTokenReady(true);
  }, [navigate, location.state]);

  const handleMfaSetupComplete = () => {
    setMfaSetup(true);
    setSetupComplete(true);
  };

  const handleCompleteSetup = async () => {
    if (!mfaSetup) {
      setError('Multi-factor authentication setup is required to complete login.');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/auth/complete-mfa-setup');
      
      // Clear the setup token
      localStorage.removeItem('mfaSetupToken');
      
      // Update with the new full access token
      updateToken(response.data.token);
      
      // Navigate to appropriate dashboard
      navigate('/dashboard', { 
        state: { 
          message: 'MFA setup complete! Your account is now secured with multi-factor authentication.' 
        }
      });
    } catch (error: any) {
      console.error('Error completing MFA setup:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          'Failed to complete MFA setup. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderMfaSetup = () => (
    <div className="glass-card max-w-2xl mx-auto p-6">
      <div className="text-center mb-6">
        {mfaMethod === 'webauthn' ? (
          <FingerprintIcon sx={{ fontSize: 48, color: '#6366f1', mb: 2 }} />
        ) : (
          <PhoneIcon sx={{ fontSize: 48, color: '#6366f1', mb: 2 }} />
        )}
        <Typography variant="h5" gutterBottom className="theme-text-primary">
          Setup Multi-Factor Authentication
        </Typography>
        <Typography variant="body2" className="theme-text-secondary mb-4">
          Choose your preferred authentication method
        </Typography>
        
        <Alert severity="info" sx={{ 
          mb: 3, 
          textAlign: 'left',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          color: '#93c5fd'
        }}>
          <Typography variant="body2">
            <strong>Enhanced Security Required:</strong> All accounts require multi-factor authentication. 
            Choose between biometric/security key or authenticator app.
          </Typography>
        </Alert>

        {/* MFA Method Toggle */}
        <ToggleButtonGroup
          value={mfaMethod}
          exclusive
          onChange={(e, newMethod) => {
            if (newMethod !== null) {
              setMfaMethod(newMethod);
              setError(''); // Clear any errors when switching
            }
          }}
          fullWidth
          sx={{ mb: 3 }}
        >
          <ToggleButton 
            value="totp"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              '&.Mui-selected': {
                backgroundColor: '#4f46e5',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4338ca'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <PhoneIcon sx={{ mr: 1 }} />
            Authenticator App
          </ToggleButton>
          <ToggleButton 
            value="webauthn"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.23)',
              '&.Mui-selected': {
                backgroundColor: '#4f46e5',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4338ca'
                }
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            <FingerprintIcon sx={{ mr: 1 }} />
            Biometric / Security Key
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {tokenReady ? (
        <>
          {mfaMethod === 'webauthn' ? (
            <WebAuthnSetup 
              onSetupComplete={handleMfaSetupComplete}
              onError={setError}
              showExisting={false}
            />
          ) : (
            <TOTPSetup 
              onSetupComplete={handleMfaSetupComplete}
              onError={setError}
            />
          )}
        </>
      ) : (
        <div className="text-center">
          <CircularProgress sx={{ color: '#6366f1' }} />
          <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
            Preparing setup...
          </Typography>
        </div>
      )}

      {error && (
        <Alert severity="error" sx={{ 
          mt: 2,
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          color: '#fca5a5',
          whiteSpace: 'pre-wrap'
        }}>
          <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
            {error}
          </Typography>
        </Alert>
      )}
    </div>
  );

  const renderComplete = () => (
    <div className="glass-card max-w-md mx-auto p-6 text-center">
      <CheckCircleIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
      <Typography variant="h5" gutterBottom className="theme-text-primary">
        MFA Setup Complete!
      </Typography>
      <Typography variant="body1" className="theme-text-secondary mb-6">
        Your account is now secured with multi-factor authentication.
      </Typography>

      <div className="mb-6">
        <div className="flex items-center justify-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <SecurityIcon sx={{ color: '#6ee7b7', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: '#6ee7b7' }}>
            Multi-Factor Authentication Enabled
          </Typography>
        </div>
      </div>

      <Button
        fullWidth
        variant="contained"
        onClick={handleCompleteSetup}
        disabled={loading}
        size="large"
        sx={{
          backgroundColor: '#4f46e5',
          '&:hover': { backgroundColor: '#4338ca' },
          py: 1.5
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
            Completing Setup...
          </>
        ) : (
          'Continue to Dashboard'
        )}
      </Button>
    </div>
  );

  return (
    <ThemeBackground variant="full">
      <Box sx={{ minHeight: '100vh', py: 4 }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 2 }}>
          {/* Header */}
          <div className="text-center mb-8">
            <Typography 
              variant="h4" 
              gutterBottom 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 600,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Security Setup Required
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1.1rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              Complete your multi-factor authentication setup to access your account.
            </Typography>
          </div>

          {/* Content */}
          <div className="mb-8">
            {setupComplete ? renderComplete() : renderMfaSetup()}
          </div>

          {/* Back to login link */}
          <div className="text-center">
            <button
              onClick={() => {
                localStorage.removeItem('mfaSetupToken');
                navigate('/login');
              }}
              className="text-sm text-blue-300 hover:text-blue-200 underline"
            >
              ← Back to login
            </button>
          </div>
        </Box>
      </Box>
    </ThemeBackground>
  );
}

export default MfaSetup;
